/**
 * The support inbox (C5's human loop, pointed the other way): a talent or an organization
 * member writes in from the app, the message lands here as a ticket, and the admin team
 * answers from the console. The shapes are the API's `AdminSupportTicketDto` family
 * (`./schemas`).
 */
import type { Covers, Schemas } from './schemas';

/**
 * Where a ticket sits. `open` is waiting on the team, `answered` is waiting on the
 * requester, `closed` is done — a reply reopens nothing, it moves `open` to `answered`;
 * only the close action moves a ticket to `closed`.
 */
export type SupportTicketStatus = Schemas['SupportTicketStatus'];
export const SUPPORT_TICKET_STATUS = {
  OPEN: 'open',
  ANSWERED: 'answered',
  CLOSED: 'closed',
} as const satisfies Record<string, SupportTicketStatus>;
const _supportTicketStatusCovered: Covers<
  SupportTicketStatus,
  (typeof SUPPORT_TICKET_STATUS)[keyof typeof SUPPORT_TICKET_STATUS]
> = true;

export const ALL_SUPPORT_TICKET_STATUSES: readonly SupportTicketStatus[] =
  Object.values(SUPPORT_TICKET_STATUS);

/** Which side of the platform the requester signs in from (C1 vs C4). */
export type SupportAccountKind = Schemas['SupportAccountKind'];

/**
 * Who wrote in. Contact details are here on purpose, unlike the admin talent search:
 * answering a support ticket sometimes means picking up the phone, and the requester
 * handed these over by writing in. `fullName` is absent until the requester has filled
 * in a profile, and `phone` for an account that signs in with an email identity.
 */
export type SupportRequester = Schemas['SupportRequesterDto'];

/**
 * What to call a requester on screen: their name once they have one, else the
 * contact they wrote in from, else `null` for the caller to fill with its own label.
 */
export function requesterDisplayName(requester: SupportRequester): string | null {
  return requester.fullName || requester.email || requester.phone || null;
}

/** One ticket. A row of the inbox list carries no `messages`; the by-id read carries them all. */
export type SupportTicket = Schemas['AdminSupportTicketDto'];

/** One row of the inbox list. */
export type SupportTicketSummary = SupportTicket;

export type SupportMessage = Schemas['SupportMessageDto'];

/**
 * One page of the inbox, in the API's shared cursor envelope. The cursor is bound to
 * the `?status=` it was issued under — sent with another status the API answers 422
 * `filter_mismatch` — so paging restarts whenever the tab changes. `meta.total` is on
 * the first page only.
 */
export type SupportTicketPage = Schemas['AdminSupportTicketListResponseDto'];

/** `POST /admin/support/tickets/:ticketId/replies`. The body is bounded server-side too. */
export type SupportReplyBody = Schemas['CreateSupportMessageDto'];

/** The longest reply the API accepts — the composer stops at the same number. */
export const SUPPORT_REPLY_MAX_LENGTH = 4000;
